
import React from 'react';
import { Button } from '@/components/ui/button';
import { FileText, Download } from 'lucide-react';

interface PatientInfo {
  name: string;
  email: string;
  phone: string;
}

interface Visit {
  id: string;
  visitNumber: number;
  title: string;
  scheduledDate: string;
  treatments: any[];
  totalCost: number;
  estimatedDuration: number;
  notes: string;
}

interface TreatmentPlan {
  patientInfo: PatientInfo;
  visits: Visit[];
  totals: {
    totalCost: number;
    totalDuration: number;
    creditsNeeded: number;
    memberSavings: number;
    creditsSufficient: boolean;
  };
}

interface PDFExportServiceV2Props {
  treatmentPlan: TreatmentPlan;
  isMember: boolean;
}

const PDFExportServiceV2 = ({ treatmentPlan, isMember }: PDFExportServiceV2Props) => {
  
  const generateLuxuryPDF = () => {
    if (!treatmentPlan.patientInfo.name) {
      alert('Please enter patient information before exporting');
      return;
    }

    // Create a new window for PDF content
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    const { patientInfo, visits, totals } = treatmentPlan;
    
    // Generate QR code placeholder for booking link
    const bookingUrl = `${window.location.origin}/book`;
    
    const pdfContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>JUV Treatment Plan - ${patientInfo.name}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              line-height: 1.5;
              color: #2c1810;
              background: white;
            }
            
            /* Luxury Header */
            .luxury-header {
              background: linear-gradient(135deg, #d4a574 0%, #b8956a 50%, #8b4513 100%);
              color: white;
              padding: 60px 40px;
              text-align: center;
              position: relative;
              overflow: hidden;
            }
            .luxury-header::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="rgba(255,255,255,0.1)"/></svg>') repeat;
              opacity: 0.3;
            }
            .luxury-header h1 {
              font-size: 42px;
              font-weight: 300;
              letter-spacing: 3px;
              margin-bottom: 12px;
              position: relative;
              z-index: 1;
            }
            .luxury-header .subtitle {
              font-size: 18px;
              opacity: 0.95;
              font-weight: 300;
              letter-spacing: 1px;
              position: relative;
              z-index: 1;
            }
            .luxury-header .logo {
              position: absolute;
              top: 20px;
              left: 40px;
              font-size: 28px;
              font-weight: bold;
              letter-spacing: 2px;
              z-index: 1;
            }
            
            .container {
              max-width: 900px;
              margin: 0 auto;
              padding: 40px;
            }
            
            /* Patient Info Card */
            .patient-card {
              background: linear-gradient(135deg, #fef7f0 0%, #f8f4f0 100%);
              padding: 30px;
              border-radius: 12px;
              margin-bottom: 40px;
              border: 1px solid #e8ddd4;
            }
            .patient-card h3 {
              color: #8b4513;
              margin-bottom: 20px;
              font-size: 20px;
              font-weight: 600;
            }
            .patient-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 15px;
            }
            .patient-field {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              border-bottom: 1px solid #e8ddd4;
            }
            .patient-field:last-child {
              border-bottom: none;
            }
            .field-label {
              font-weight: 600;
              color: #8b4513;
            }
            .field-value {
              color: #2c1810;
            }
            
            /* Timeline Section */
            .timeline-header {
              text-align: center;
              margin: 50px 0 30px 0;
            }
            .timeline-header h2 {
              color: #8b4513;
              font-size: 28px;
              font-weight: 600;
              margin-bottom: 10px;
            }
            .timeline-header p {
              color: #6b5b73;
              font-size: 16px;
            }
            
            /* Visit Timeline */
            .timeline {
              position: relative;
              margin: 40px 0;
            }
            .timeline::before {
              content: '';
              position: absolute;
              left: 30px;
              top: 0;
              bottom: 0;
              width: 2px;
              background: linear-gradient(to bottom, #d4a574, #b8956a);
            }
            
            .visit-block {
              position: relative;
              margin-bottom: 40px;
              padding-left: 80px;
            }
            .visit-number {
              position: absolute;
              left: 15px;
              top: 15px;
              width: 30px;
              height: 30px;
              background: linear-gradient(135deg, #d4a574, #b8956a);
              color: white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: bold;
              font-size: 14px;
              box-shadow: 0 4px 12px rgba(212, 165, 116, 0.3);
            }
            
            .visit-card {
              background: white;
              border: 1px solid #e8ddd4;
              border-radius: 12px;
              padding: 25px;
              box-shadow: 0 4px 20px rgba(0,0,0,0.08);
            }
            .visit-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 20px;
              padding-bottom: 15px;
              border-bottom: 1px solid #f0f0f0;
            }
            .visit-title {
              font-size: 18px;
              font-weight: 600;
              color: #8b4513;
            }
            .visit-meta {
              display: flex;
              gap: 15px;
              font-size: 14px;
              color: #6b5b73;
            }
            .visit-meta span {
              display: flex;
              align-items: center;
              gap: 5px;
            }
            
            .treatments-list {
              margin: 20px 0;
            }
            .treatment-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 15px 0;
              border-bottom: 1px solid #f8f4f0;
            }
            .treatment-item:last-child {
              border-bottom: none;
            }
            .treatment-info h4 {
              color: #2c1810;
              font-weight: 600;
              margin-bottom: 4px;
            }
            .treatment-meta {
              font-size: 13px;
              color: #6b5b73;
            }
            .treatment-price {
              text-align: right;
              font-weight: 700;
              color: #d4a574;
              font-size: 16px;
            }
            .member-badge {
              display: inline-block;
              background: #22c55e;
              color: white;
              padding: 2px 8px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: 600;
              margin-left: 8px;
            }
            
            /* Summary Section */
            .luxury-summary {
              background: linear-gradient(135deg, #fef7f0 0%, #f8f4f0 100%);
              border: 2px solid #d4a574;
              border-radius: 16px;
              padding: 35px;
              margin: 50px 0;
              position: relative;
            }
            .luxury-summary::before {
              content: '✨';
              position: absolute;
              top: -15px;
              left: 35px;
              background: white;
              padding: 5px 10px;
              font-size: 20px;
            }
            .summary-title {
              color: #8b4513;
              font-size: 24px;
              font-weight: 600;
              text-align: center;
              margin-bottom: 30px;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px;
              margin-bottom: 25px;
            }
            .summary-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 12px 0;
              border-bottom: 1px solid #e8ddd4;
            }
            .summary-item:last-child {
              border-bottom: none;
            }
            .summary-label {
              font-weight: 600;
              color: #2c1810;
            }
            .summary-value {
              font-weight: 700;
              color: #d4a574;
            }
            .summary-total {
              border-top: 2px solid #d4a574;
              margin-top: 20px;
              padding-top: 20px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .summary-total .label {
              font-size: 20px;
              font-weight: 700;
              color: #8b4513;
            }
            .summary-total .value {
              font-size: 24px;
              font-weight: 700;
              color: #8b4513;
            }
            .member-savings {
              color: #22c55e !important;
            }
            
            /* QR and Footer */
            .footer-section {
              margin-top: 60px;
              text-align: center;
              border-top: 1px solid #e8ddd4;
              padding-top: 40px;
            }
            .qr-container {
              display: flex;
              justify-content: center;
              align-items: center;
              gap: 30px;
              margin: 30px 0;
            }
            .qr-placeholder {
              width: 120px;
              height: 120px;
              background: linear-gradient(135deg, #f8f4f0, #e8ddd4);
              border: 2px dashed #d4a574;
              border-radius: 12px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 12px;
              color: #8b4513;
              text-align: center;
              line-height: 1.3;
            }
            .qr-info {
              max-width: 300px;
            }
            .qr-info h4 {
              color: #8b4513;
              font-weight: 600;
              margin-bottom: 8px;
            }
            .qr-info p {
              color: #6b5b73;
              font-size: 14px;
              line-height: 1.4;
            }
            
            .footer-branding {
              margin-top: 40px;
            }
            .footer-branding h3 {
              color: #8b4513;
              font-size: 24px;
              font-weight: 700;
              letter-spacing: 2px;
              margin-bottom: 8px;
            }
            .footer-branding p {
              color: #6b5b73;
              font-size: 14px;
              margin-bottom: 4px;
            }
            .footer-disclaimer {
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px solid #e8ddd4;
              font-size: 12px;
              color: #6b5b73;
              font-style: italic;
              line-height: 1.4;
            }
            
            @media print {
              .luxury-header, .visit-block, .luxury-summary { break-inside: avoid; }
              body { print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="luxury-header">
            <div class="logo">JUV</div>
            <h1>Treatment Plan</h1>
            <p class="subtitle">Your Personalized Aesthetic Journey</p>
          </div>
          
          <div class="container">
            <div class="patient-card">
              <h3>Patient Information</h3>
              <div class="patient-grid">
                <div class="patient-field">
                  <span class="field-label">Name:</span>
                  <span class="field-value">${patientInfo.name}</span>
                </div>
                <div class="patient-field">
                  <span class="field-label">Email:</span>
                  <span class="field-value">${patientInfo.email}</span>
                </div>
                <div class="patient-field">
                  <span class="field-label">Phone:</span>
                  <span class="field-value">${patientInfo.phone}</span>
                </div>
                <div class="patient-field">
                  <span class="field-label">Membership:</span>
                  <span class="field-value">${isMember ? 'JUV Member ✨' : 'Non-Member'}</span>
                </div>
              </div>
            </div>
            
            <div class="timeline-header">
              <h2>Treatment Timeline</h2>
              <p>Your journey to transformation, beautifully planned</p>
            </div>
            
            <div class="timeline">
              ${visits.map((visit, index) => {
                const visitIcon = visit.treatments.some(t => t.category?.includes('Smile')) ? '🦷' : 
                                visit.treatments.some(t => t.category?.includes('Skin')) ? '✨' : '💉';
                
                return `
                  <div class="visit-block">
                    <div class="visit-number">${visit.visitNumber}</div>
                    <div class="visit-card">
                      <div class="visit-header">
                        <div>
                          <div class="visit-title">${visitIcon} ${visit.title}</div>
                          ${visit.scheduledDate ? `<div style="color: #6b5b73; font-size: 14px; margin-top: 4px;">Scheduled: ${new Date(visit.scheduledDate).toLocaleDateString()}</div>` : ''}
                        </div>
                        <div class="visit-meta">
                          <span>⏱️ ${visit.estimatedDuration} min</span>
                          <span>💳 ${Math.ceil(visit.totalCost / 50)} credits</span>
                          <span>💰 $${visit.totalCost.toFixed(2)}</span>
                        </div>
                      </div>
                      
                      ${visit.treatments.length > 0 ? `
                        <div class="treatments-list">
                          ${visit.treatments.map(treatment => `
                            <div class="treatment-item">
                              <div class="treatment-info">
                                <h4>${treatment.name}${treatment.quantity > 1 ? ` × ${treatment.quantity}` : ''}</h4>
                                <div class="treatment-meta">
                                  ${treatment.duration_minutes} min • ${treatment.category}
                                  ${isMember && treatment.member_price < treatment.retail_price ? 
                                    `<span class="member-badge">Member Price</span>` : ''}
                                </div>
                              </div>
                              <div class="treatment-price">
                                $${(treatment.applied_price * treatment.quantity).toFixed(2)}
                                ${isMember && treatment.member_price < treatment.retail_price ? 
                                  `<div style="font-size: 12px; color: #22c55e;">Save $${((treatment.retail_price - treatment.member_price) * treatment.quantity).toFixed(2)}</div>` : ''}
                              </div>
                            </div>
                          `).join('')}
                        </div>
                      ` : ''}
                      
                      ${visit.notes ? `
                        <div style="margin-top: 15px; padding: 15px; background: #f8f4f0; border-radius: 8px; border-left: 3px solid #d4a574;">
                          <strong style="color: #8b4513;">Notes:</strong> ${visit.notes}
                        </div>
                      ` : ''}
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
            
            <div class="luxury-summary">
              <h3 class="summary-title">Investment Summary</h3>
              <div class="summary-grid">
                <div class="summary-item">
                  <span class="summary-label">Total Visits:</span>
                  <span class="summary-value">${visits.length}</span>
                </div>
                <div class="summary-item">
                  <span class="summary-label">Total Duration:</span>
                  <span class="summary-value">${totals.totalDuration} minutes</span>
                </div>
                <div class="summary-item">
                  <span class="summary-label">Credits Required:</span>
                  <span class="summary-value">${totals.creditsNeeded}</span>
                </div>
                <div class="summary-item">
                  <span class="summary-label">Cash Equivalent:</span>
                  <span class="summary-value">$${(totals.creditsNeeded * 50).toLocaleString()}</span>
                </div>
              </div>
              
              ${totals.memberSavings > 0 ? `
                <div class="summary-item">
                  <span class="summary-label">Member Savings:</span>
                  <span class="summary-value member-savings">$${totals.memberSavings.toFixed(2)}</span>
                </div>
              ` : ''}
              
              <div class="summary-total">
                <span class="label">Total Investment:</span>
                <span class="value">$${totals.totalCost.toFixed(2)}</span>
              </div>
            </div>
            
            <div class="footer-section">
              <div class="qr-container">
                <div class="qr-placeholder">
                  QR Code<br>
                  <small>Scan to Book</small>
                </div>
                <div class="qr-info">
                  <h4>Ready to Begin?</h4>
                  <p>Scan the QR code or visit <strong>juv.com/book</strong> to schedule your consultation and start your transformation journey.</p>
                  <p style="margin-top: 10px;"><strong>Plan ID:</strong> ${Date.now().toString(36)}</p>
                </div>
              </div>
              
              <div class="footer-branding">
                <h3>JUV</h3>
                <p>Luxury Aesthetic Treatments & Wellness</p>
                <p><strong>Book:</strong> juv.com/book • <strong>Membership:</strong> juv.com/membership</p>
              </div>
              
              <div class="footer-disclaimer">
                *Prices and treatment plans are subject to consultation and provider assessment. 
                Individual results may vary. This plan serves as an estimate and may be adjusted 
                based on your unique needs and goals during your consultation.
              </div>
            </div>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(pdfContent);
    printWindow.document.close();
    
    // Auto-print after content loads
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  return (
    <Button
      onClick={generateLuxuryPDF}
      className="juv-button-primary w-full"
      size="lg"
    >
      <FileText className="w-5 h-5 mr-2" />
      Export Luxury Treatment Plan
    </Button>
  );
};

export default PDFExportServiceV2;
